import { useEffect, useState } from 'react';
import requiredFieldConfigRaw from '../../data/requiredFieldConfig';
import { fieldReader, fieldWriter, getTpoOrganizationById } from '../../services/loanApi';
import { getAccessToken } from '../../services/authApi';
import { getZipLocations } from '../../services/locationServiceApi';
import US_STATES from '../../data/usStates';
import Toast from '../shared/Toast';
import LoadingOverlay from '../shared/LoadingOverlay';
import { menuItemsCP, menuItemsBP } from '../menu/menuItems';
import { checkValidationRequired } from '../../utils/validationRedirect';
import { resolveFieldOptions } from '../../utils/loanProgramUtil';

const AppraisalValidationRenderer = ({ pageName, config, redirectPage }) => {
    const [token, setToken] = useState(null);
    const [loanId, setLoanId] = useState('');
    const [fieldValues, setFieldValues] = useState({});
    const [loading, setLoading] = useState(true);
    const [loadingText, setLoadingText] = useState('Loading...');
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    const [showZipPopup, setShowZipPopup] = useState(false);
    const [cityOptions, setCityOptions] = useState([]);
    const [zipSelection, setZipSelection] = useState({ cityId: '', stateId: '' });

    const [pageConfig, setPageConfig] = useState(null);

    const [toastVisible, setToastVisible] = useState(false);
    const [toastAnimation, setToastAnimation] = useState("animate-fade-in-right");
    const [showRequestDisclosures, setRequestDisclosures] = useState(false);
    const [showSendDisclosures, setShowSendDisclosures] = useState(false);
    const [brokerOptions, setbrokerOptions] = useState('');
    const [skipModuleValidations, setSkipModuleValidations] = useState(false);

    const init = async () => {
        try {
            window.elli?.script?.guest?.create?.();
            const loanObject = await window.elli.script.getObject("loan");
            const loan = await loanObject.all();
            setLoanId(loan.id);

            const authObject = await window.elli.script.getObject("auth");
            const authCode = await authObject.createAuthCode();
            const accessToken = await getAccessToken(authCode);
            setToken(accessToken);

            const fieldIds = [
                ...new Set([
                    "CX.LOAN.TYPE",
                    "CX.TPOC.OVERRIDE.VALIDATIONS",
                    "3238",
                    "TPO.X15",
                    ...config.FieldGroups.flatMap(group =>
                        group.Fields
                            .filter(f => f.id)
                            .map(f => f.id)
                    )
                ])
            ];
            const values = await fieldReader(accessToken, fieldIds, loan.id);

            const overrideValidations =
                (values["CX.TPOC.OVERRIDE.VALIDATIONS"] || "")
                    .toString()
                    .trim()
                    .toUpperCase();

            const skipModules = overrideValidations === "Y";
            setSkipModuleValidations(skipModules);

            const loanType = values["CX.LOAN.TYPE"];
            // start with groups filtered by loan type only
            let filteredGroups = config.FieldGroups.filter(
                g => !g.LoanType || g.LoanType === loanType
            );

            if (skipModules) {
                filteredGroups = filteredGroups.filter(
                    g => (g.Type || "").toLowerCase() !== "module"
                );
            }

            let newGroups = filteredGroups;

            if (!skipModules) {
                // get tpo organization details to decide whether to show the Send Disclosure module
                const tpoId = values["TPO.X15"];

                // default: do not include the Send Disclosure module
                let includeSendDisclosureGroup = false;
                let sendDisclosureRedirect = null;

                if (!tpoId) {
                    // No TPO configured — treat as "generate" (original behavior was showing send disclosures if no tpo)
                    includeSendDisclosureGroup = true;
                    sendDisclosureRedirect = "";
                } else {
                    try {
                        const tpoAuthObject = await window.elli.script.getObject("auth");
                        const tpoAuthCodes = await tpoAuthObject.createAuthCode();
                        const tpoToken = await getAccessToken(tpoAuthCodes);
                        const tpoOrganization = await getTpoOrganizationById(tpoToken, tpoId);

                        if (!tpoOrganization || tpoOrganization.notFound) {
                            includeSendDisclosureGroup = true;
                            sendDisclosureRedirect = "Send Disclosures";
                        } else {
                            const brokerOptions = (tpoOrganization.brokerOptions || '').toLowerCase();
                            setbrokerOptions(tpoOrganization.brokerOptions);

                            if (brokerOptions === 'generateleanddisclosures') {
                                includeSendDisclosureGroup = true;
                                sendDisclosureRedirect = "Send Disclosures";
                            } else if (brokerOptions === 'requestleanddisclosures') {
                                includeSendDisclosureGroup = true;
                                sendDisclosureRedirect = "Disclosures";
                            } else {
                                includeSendDisclosureGroup = false;
                                sendDisclosureRedirect = null;
                            }
                        }
                    } catch (tpoErr) {
                        // If TPO fetch fails, fall back to showing Send Disclosures (safe/default behavior)
                        console.error('Failed to fetch TPO org, defaulting to show Send Disclosures', tpoErr);
                        includeSendDisclosureGroup = true;
                        sendDisclosureRedirect = "";
                    }
                }

                // and if included, overwrite its pagetoRedirect value as required.
                newGroups = filteredGroups.reduce((acc, g) => {
                    if ((g.Group || '').toLowerCase() === 'send disclosure') {
                        if (!includeSendDisclosureGroup) {
                            return acc;
                        } else {
                            const cloned = JSON.parse(JSON.stringify(g));

                            if (sendDisclosureRedirect === "Send Disclosures") {
                                cloned.Notes = "Please send disclosures before ordering the appraisal.";
                                cloned.Fields = cloned.Fields.map(f => ({
                                    ...f,
                                    pagetoRedirect: "Send Disclosures",
                                    name: "Send Disclosure"
                                }));
                            } else if (sendDisclosureRedirect === "Disclosures") {
                                cloned.Notes = "Disclosures should be sent before ordering the appraisal.";
                                cloned.Fields = cloned.Fields.map(f => ({
                                    ...f,
                                    pagetoRedirect: "Disclosures",
                                    name: "Request Disclosure"
                                }));
                            } else {
                                cloned.Notes = "Disclosures should be sent before ordering the appraisal.";
                                cloned.Fields = cloned.Fields.map(f => ({
                                    ...f,
                                    pagetoRedirect: "",
                                    name: ""
                                }));
                            }

                            acc.push(cloned);
                            return acc;
                        }
                    } else {
                        acc.push(g);
                        return acc;
                    }
                }, []);
            }

            setPageConfig({ ...config, FieldGroups: newGroups });
            setFieldValues(values);

        } catch (error) {
            console.error(`Error initializing ${pageName} page:`, error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        init();
    }, []);

    const shouldShowGroup = (group) => {
        if (skipModuleValidations && group.Type === "Module") {
            return false;
        }

        if (group.Type === "Module") {
            return !isModuleGroupCleared(group); // show if module has missing fields
        }

        if (group.Type === "Field") {
            return group.Fields.some(field => {
                if (field.hidden) return false;
                const value = fieldValues[field.id];
                return isEmptyField(value);
            });
        }

        return false;
    };

    const handleSave = async () => {
        const updated = [];
        const missingFields = [];

        pageConfig?.FieldGroups
            .filter(group => group.Type === 'Field' && shouldShowGroup(group))
            .forEach(group => {
                group.Fields.forEach(field => {
                    if (field.hidden) return;

                    const el = document.getElementById(`field-${field.id}`);
                    if (!el) return;

                    const uiValue = el.value?.trim() ?? '';
                    const originalValue = fieldValues[field.id]?.trim() ?? '';

                    //Only save if the field is visible, changed, and not empty
                    const isVisible = !field.hidden;
                    const isChanged = uiValue !== originalValue;
                    const isNotEmpty = uiValue !== '';

                    if (isVisible && isChanged && isNotEmpty) {
                        updated.push({ id: field.id, value: uiValue });
                    }

                    // For toast messaging
                    if (uiValue === '') {
                        missingFields.push(field.name);
                    }
                });
            });


        if (missingFields.length > 0) {
            showToastMsg(
                `The following required fields are missing:<br>- ${missingFields.join('<br>- ')}`
            );
            return;
        }

        // Save and redirect if nothing is missing
        setLoading(true);
        setLoadingText("Saving...");
        try {
            await fieldWriter(token, loanId, updated);

            if (!allModulesCleared()) {
                init();
                if (redirectPage === "Product Pricing & Lock") {
                    showToastMsg("Please order Credit before Pricing.");
                }
                else {
                    showToastMsg("Disclosures should be sent before ordering the appraisal.");
                }
            } else {
                await redirectToPage(redirectPage, loanId);
            }

        } catch (err) {
            console.log("Save failed", err);
            if (err.response?.data?.errors?.length > 0) {
                const errors = err.response.data.errors;
                const formattedErrors = errors
                    .map(e => `• ${e.details || e.summary}`)
                    .join('<br>');

                showToastMsg(`Save failed:<br>${formattedErrors}`);
            } else if (err.response?.data?.details) {
                showToastMsg(`Save failed: ${err.response.data.details}`);
            } else {
                showToastMsg("Save failed due to an unexpected error.");
            }
        }
        finally {
            setLoading(false);
        }
    };


    const showToastMsg = (message) => {
        setToastMessage(message);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 10000);
    };

    const handleZipBlur = async (field) => {
        if (field.datatype !== 'Zip') return;
        const zip = document.getElementById(`field-${field.id}`)?.value;
        if (!zip || zip.length < 5) return;

        const results = await getZipLocations(zip, token);
        const { city: cityId, state: stateId } = field;

        if (results.length === 1) {
            document.getElementById(`field-${cityId}`).value = results[0].city;
            document.getElementById(`field-${stateId}`).value = results[0].abbrev;
        } else if (results.length > 1) {
            setCityOptions(results);
            setZipSelection({ cityId, stateId });
            setShowZipPopup(true);
        }
    };

    const isEmptyField = (val) => {
        const trimmed = (val || '').trim();
        return trimmed === '' || trimmed === '//' || trimmed === '  /  /    ';
    };

    const isModuleGroupCleared = (group) => {
        if (skipModuleValidations && group.Type === "Module") {
            return true;
        }

        return group.Fields.every(field => {
            const value = fieldValues[field.id];
            return !isEmptyField(value);
        });
    };

    const allFieldsCleared = () => {
        let isCleared = pageConfig?.FieldGroups
            .filter(group => group.Type === 'Field')
            .every(group => isModuleGroupCleared(group));

        return isCleared;
    };

    const allModulesCleared = () => {
        if (skipModuleValidations) return true;

        return pageConfig?.FieldGroups
            .filter(group => group.Type === 'Module')
            .every(group => isModuleGroupCleared(group));
    };


    useEffect(() => {
        let hideTimer, vanishTimer;

        if (showToast) {
            setToastVisible(true);
            setToastAnimation("animate-fade-in-right");

            hideTimer = setTimeout(() => {
                setToastAnimation("animate-fade-out-right");
                vanishTimer = setTimeout(() => hideToast(), 500);
            }, 4000);
        }

        return () => {
            clearTimeout(hideTimer);
            clearTimeout(vanishTimer);
        };
    }, [showToast]);

    const hideToast = () => {
        setShowToast(false);
        setToastVisible(false);
    };

    const redirectToPage = async (page, loanId) => {
        const tpoAppObject = await window.elli.script.getObject("tpoApplication");

        const loanTypeValue = fieldValues["CX.LOAN.TYPE"]?.toLowerCase() || "";
        const loanOfficerNMLSID = fieldValues["3238"]?.trim(); // NMLS Field ID

        const isBusinessLoan = loanTypeValue.includes("business");
        const menuItems = isBusinessLoan ? menuItemsBP : menuItemsCP;

        // Find menu item
        let menuItem = menuItems.find(item => item.displayName === page || item.label === page);

        // Check subsections if not found in top-level
        if (!menuItem) {
            for (const parent of menuItems) {
                if (parent.subsections) {
                    const sub = parent.subsections.find(subItem => subItem.displayName === page || subItem.label === page);
                    if (sub) {
                        menuItem = sub;
                        break;
                    }
                }
            }
        }

        // Override CorelogicCredco → DUAL AUS for Business Loan + NMLS ID
        if (isBusinessLoan && loanOfficerNMLSID && menuItem?.label === "CorelogicCredco") {
            menuItem = menuItemsCP.find(item => item.label === "DUAL AUS"); // fallback to CP for DUAL AUS config
        }

        // Perform validation check only if config is present
        if (menuItem?.validationExtPageId && menuItem?.validationPage) {
            const needsValidation = await checkValidationRequired(menuItem, loanId);
            if (needsValidation) {
                await tpoAppObject.pageRedirect("ExternalLoanPage", {
                    id: loanId,
                    extpageid: menuItem.validationExtPageId,
                    pagename: menuItem.validationPage
                });
                return;
            }
        }

        // Redirect to external page if extPageId is present
        if (menuItem?.extPageId) {
            await tpoAppObject.pageRedirect("ExternalLoanPage", {
                id: loanId,
                extpageid: menuItem.extPageId,
                pagename: menuItem.label
            });
        } else {
            // Default internal redirection
            await tpoAppObject.pageRedirect(menuItem.label, { id: loanId });
        }
    };

    return (
        <>
            <LoadingOverlay loading={loading} text={loadingText} />

            <Toast
                visible={toastVisible}
                type="warning"
                message={toastMessage}
                onClose={hideToast}
                animationClass={toastAnimation}
            />

            {showZipPopup && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" onClick={() => setShowZipPopup(false)}>
                    <div className="bg-white rounded-lg p-6 w-96 shadow-lg" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold">Select City & State</h3>
                            <button className="text-gray-500 hover:text-black" onClick={() => setShowZipPopup(false)}>×</button>
                        </div>
                        <ul className="space-y-2">
                            {cityOptions.map((opt, idx) => (
                                <li key={idx}>
                                    <button
                                        onClick={() => {
                                            document.getElementById(`field-${zipSelection.cityId}`).value = opt.city;
                                            document.getElementById(`field-${zipSelection.stateId}`).value = opt.abbrev;
                                            setShowZipPopup(false);
                                        }}
                                        className="w-full text-left px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded"
                                    >
                                        {opt.city}, {opt.abbrev}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            <div className="space-y-10 p-25px">
                <div className="rounded-lg p-3 space-y-6 max-w-8xl mx-auto">
                    <h2 className="page-title">{pageConfig?.Header}</h2>

                    {!loading && (
                        <>
                            {/* Show Module Section only if Module is not cleared */}
                            {!allModulesCleared() && (
                                pageConfig?.FieldGroups
                                    .filter(group => group.Type === 'Module')
                                    .map((group, index) => (
                                        <div key={index} className="border border-blue-200 rounded bg-gray-50 mt-5">
                                            <div className="section-header">{group.Group}</div>
                                            {group.Notes && (
                                                <div className="text-sm text-red-700 font-semibold italic px-4 pb-1 pt-2 whitespace-pre-line">
                                                    {group.Notes}
                                                </div>
                                            )}
                                            <div className="grid md:grid-cols-2 gap-6 p-4">
                                                {group.Fields.map(field => (
                                                    <div key={field.id}>
                                                        <input
                                                            type="text"
                                                            className="hidden"
                                                            id={`field-${field.id}`}
                                                            defaultValue={fieldValues[field.id]}
                                                        />
                                                        {field.name && (
                                                            <button
                                                                type="button"
                                                                className="mt-2 btn em-btn-tertiary"
                                                                onClick={() => redirectToPage(field.pagetoRedirect, loanId)}
                                                            >
                                                                {field.name}
                                                            </button>
                                                        )}

                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))
                            )}

                            {!allFieldsCleared() && (
                                <>
                                    <div>
                                        <div className="flex justify-between items-center mt-4 mb-2">
                                            <p className="text-sm text-red-600 italic mb-2">All fields are required to {pageName}.</p>
                                        </div>

                                        {pageConfig?.FieldGroups
                                            .filter(group => shouldShowGroup(group) && group.Type === 'Field')
                                            .map((group, index) => (
                                                <div key={index} className="border border-blue-200 rounded bg-gray-50 mt-5">
                                                    <div className="section-header">{group.Group}</div>

                                                    <div className="grid md:grid-cols-2 gap-6 p-4">
                                                        {group.Fields
                                                            .filter(field => !field.hidden)
                                                            .filter(field => {
                                                                // Always show fields in condition-based groups
                                                                if (group.ShowIf) return true;
                                                                const value = fieldValues[field.id];
                                                                return isEmptyField(value);
                                                            })
                                                            .map(field => (
                                                                <div key={field.id}>
                                                                    {field.datatype !== 'button' && (
                                                                        <label className="form-label mb-1" htmlFor={`field-${field.id}`}>
                                                                            {field.name} <span className="text-red-600">*</span>
                                                                        </label>
                                                                    )}

                                                                    {field.datatype === 'State' ? (
                                                                        <select className="form-input w-full text-sm border rounded p-1" id={`field-${field.id}`} defaultValue="">
                                                                            <option value="">Select State</option>
                                                                            {US_STATES.map(state => (
                                                                                <option key={state.abbrev} value={state.abbrev}>
                                                                                    {state.name}
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                    ) : field.datatype === 'Date' ? (
                                                                        <input
                                                                            type="date"
                                                                            className="form-input-medium"
                                                                            id={`field-${field.id}`}
                                                                            defaultValue={fieldValues[field.id] ? new Date(fieldValues[field.id]).toISOString().split('T')[0] : ''}
                                                                        />

                                                                    ) : field.datatype === 'dropdown' ? (
                                                                        <select
                                                                            className="form-input w-full text-sm border rounded p-1"
                                                                            id={`field-${field.id}`}
                                                                            defaultValue={fieldValues[field.id] || ""}
                                                                        >
                                                                            <option value="">Select an Option</option>
                                                                            {/* resolveFieldOptions turns canonical loan-program
                                                                                codes into this org's product names and drops the
                                                                                products it does not carry. */}
                                                                            {resolveFieldOptions(field).map(opt => (
                                                                                <option key={opt.value} value={opt.value}>
                                                                                    {opt.text}
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                    ) : (
                                                                        <input
                                                                            type="text"
                                                                            className="form-input"
                                                                            id={`field-${field.id}`}
                                                                            defaultValue={fieldValues[field.id]}
                                                                            onBlur={() => field.datatype === 'Zip' && handleZipBlur(field)}
                                                                        />
                                                                    )}
                                                                </div>
                                                            ))}
                                                    </div>
                                                </div>
                                            ))}

                                        <div className="text-right mt-8">
                                            <button onClick={handleSave} className="btn em-btn-primary">Save</button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
};


const AppraisalValidate = () => {
    const config = requiredFieldConfigRaw.Pages.find(p => p.Page === 'Order Appraisal');
    return <AppraisalValidationRenderer pageName="Order Appraisal" config={config} redirectPage="Service Orders" />;
};

export default AppraisalValidate;